import { useState } from 'react';
import classnames from 'classnames';
import { Link, useHistory, useLocation } from 'react-router-dom';
import gql from 'graphql-tag';
import { useQuery } from '@apollo/react-hooks';

import { Accordion, AccordionButton, AccordionItem, AccordionPanel } from '@reach/accordion';
import '@reach/accordion/styles.css';
import { FaCircle, FaChevronDown } from 'react-icons/fa';

import ReactSelect from '../../../components/form/ReactSelect';
import Loading from '../../../components/Loading';
import { useQueryString } from '../AudiobookDetail';
import { usePermissions } from '../../../hooks/usePermissions';
import { userGroups } from '../../../context/AuthContext';

Object.defineProperty(URLSearchParams.prototype, 'setAll', {
    value: function URLSearchParamsSetAll(key, values) {
        let S = this;
        S.delete(key);
        values.forEach((val) => {
            S.append(key, val);
        });
    },
    writable: true,
    enumerable: false,
    configurable: true
});

const GET_PRODUCT_DIAGNOSTIC_RESULTS = gql`
    query productDiagnosticsResults($contentId: String!) {
        productDiagnosticsResults(contentId: $contentId) {
            platform
            business_model
            batch_date
            status
            reason
        }
    }
`;

const ProductSummary = ({ contentId }) => {
    const location = useLocation();
    const history = useHistory();
    const queryString = useQueryString(location.search);

    const isSpotifyUser = usePermissions(userGroups.SPOTIFY);

    const { loading, data, error } = useQuery(GET_PRODUCT_DIAGNOSTIC_RESULTS, {
        variables: { contentId }
    });

    let partnerOptions = [];

    // remove partnerOption duplicates
    if (data) {
        partnerOptions = [
            ...new Set(
                data.productDiagnosticsResults
                    .filter((p) => p?.platform !== 'platform_24symbols' && p?.platform !== 'platform_ebsco')
                    .map((result) => result?.platform) || []
            )
        ];
    }

    let selectedPartners = isSpotifyUser ? ['platform_spotify'] : queryString.getAll('partner');
    let [selectedStatus, setSelectedStatus] = useState({});
    let [hasProductFilter, setHasProductFilter] = useState({});

    const handlePartnersChange = (v) => {
        if (!v || !v.length) {
            history.push(location.pathname);
            return;
        }
        queryString.setAll(
            'partner',
            v.map((p) => p.value)
        );
        history.push({ pathname: location.pathname, search: queryString.toString() });
    };

    return (
        <div>
            <h3 className="mb-4">Product Summary</h3>
            {data && !loading && (
                <>
                    {!isSpotifyUser && (
                        <p className="mb-6 txt-italic">This service is not available for Ebsco or 24Symbols</p>
                    )}
                    <div className="row col-xs-12 bottom-sm pl-0 mb-4">
                        {!isSpotifyUser && (
                            <div className="col-xs-12 col-lg-4">
                                <ReactSelect
                                    label="Partners"
                                    id="rs-1"
                                    isMulti={true}
                                    value={selectedPartners
                                        .filter(
                                            (p) =>
                                                p?.platform !== 'platform_24symbols' && p?.platform !== 'platform_ebsco'
                                        )
                                        .map((v, i) => ({
                                            label: v,
                                            value: v
                                        }))}
                                    options={partnerOptions.map((v, i) => ({
                                        label: v.split('_').splice(1).join(' '),
                                        value: v
                                    }))}
                                    className="p-0"
                                    onChange={handlePartnersChange}
                                    isClearable={true}
                                    placeholder="All"
                                />
                            </div>
                        )}

                        <div className="col-xs-12 col-lg-4">
                            <ReactSelect
                                label="Status"
                                id="rs-1"
                                isMulti={false}
                                className="p-0"
                                value={{ label: selectedStatus.label || 'All', value: selectedStatus.value || '' }}
                                options={[
                                    { value: 'activated_product', label: 'Activated Product' },
                                    {
                                        value: 'deactivated_product',
                                        label: 'Deactivated Product'
                                    },
                                    { value: 'replaced_product', label: 'Replaced Product' },
                                    { value: 'no_product', label: 'No Product' },
                                    { value: 'same_product', label: 'Same Product' }
                                ]}
                                onChange={(v) => setSelectedStatus(v || {})}
                                isClearable={true}
                                placeholder="All"
                            />
                        </div>
                        <div className="col-xs-12 col-lg-4">
                            <ReactSelect
                                label="Has Product?"
                                id="rs-1"
                                isMulti={false}
                                className="p-0"
                                value={{ label: hasProductFilter.label || 'All', value: hasProductFilter.value || '' }}
                                options={[
                                    { value: 'yes', label: 'Yes' },
                                    {
                                        value: 'no',
                                        label: 'No'
                                    }
                                ]}
                                onChange={(v) => setHasProductFilter(v || {})}
                                isClearable={true}
                                placeholder="All"
                            />
                        </div>
                    </div>
                </>
            )}
            {loading && <Loading />}
            {data && !loading && !error && (
                <table className="table table-sm">
                    <thead>
                        <tr>
                            <th>Partner</th>
                            <th>Model</th>
                            <th>Has Product?</th>
                            <th>Product Diagnostic Status</th>
                            <th>Note</th>
                        </tr>
                    </thead>
                    <tbody>
                        {data?.productDiagnosticsResults
                            ?.filter((p) => p?.platform !== 'platform_24symbols' && p?.platform !== 'platform_ebsco')
                            ?.filter((t) => {
                                return selectedStatus.value === t.status || !selectedStatus.value;
                            })
                            .filter((t) => {
                                return !selectedPartners.length || selectedPartners.includes(t.platform);
                            })
                            .filter((t) => {
                                return (
                                    !hasProductFilter?.value ||
                                    (hasProductFilter?.value === 'yes' &&
                                        [
                                            'same_product',
                                            'replaced_product',
                                            'activated_product',
                                            'deactivated_product'
                                        ].includes(t.status)) ||
                                    (hasProductFilter?.value === 'no' && t.status === 'no_product')
                                );
                            })
                            .map((result, idx) => (
                                <tr key={idx}>
                                    <td data-title="Partner">
                                        <Link
                                            to={`/title-management/${contentId}/product-history/${result.platform.toLocaleLowerCase()}`}
                                            style={{ textTransform: 'capitalize' }}
                                        >
                                            {result.platform.split('_').splice(1).join(' ')}
                                        </Link>
                                    </td>
                                    <td data-title="Model">{result.business_model}</td>
                                    <td data-title="Has Product?">
                                        {result.status === 'same_product' ||
                                        result.status === 'replaced_product' ||
                                        result.status === 'activated_product' ? (
                                            <>
                                                Yes <FaCircle color={'#239930'} className="ml-1" />
                                            </>
                                        ) : (
                                            <>
                                                No <FaCircle color={'#ffdc00 '} className="ml-2" />
                                            </>
                                        )}
                                    </td>
                                    <td data-title="Product Diagnostic Status">
                                        <span
                                            className={classnames('badge p-1 status', {
                                                'badge-danger': result.status === 'deactivated_product',
                                                'badge-blue': result.status === 'activated_product',
                                                'badge-info': result.status === 'replaced_product',
                                                'badge-green': result.status === 'same_product',
                                                'badge-warning': result.status === 'no_product'
                                            })}
                                        >
                                            {result.status.split('_').join(' ')}
                                        </span>
                                    </td>
                                    <td data-title="Note">{result.reason}</td>
                                </tr>
                            ))}
                    </tbody>
                </table>
            )}
            {!data && !error && !loading && <p>No Results</p>}
            {error?.message && (
                <div className="badge badge-outline-danger">
                    <p>{error?.message}</p>
                </div>
            )}
            <section className="mt-24">
                <Accordion collapsible defaultIndex={0}>
                    <AccordionItem className="accordionItem">
                        <h3 className="row">
                            <AccordionButton className="btn col-xs-12 txt-left p-2 txt-bold">
                                Status Glossary
                                <FaChevronDown className="ml-1" />
                            </AccordionButton>
                        </h3>
                        <AccordionPanel>
                            <table className="table table-sm">
                                <thead>
                                    <tr>
                                        <th>Status</th>
                                        <th>Description</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td data-title="Status">
                                            <span className="badge badge-green p-1 status">Same Product</span>
                                        </td>

                                        <td data-title="Description">
                                            There was already an existing active product, and the information has stayed
                                            the same. No need for an update.
                                        </td>
                                    </tr>
                                    <tr>
                                        <td data-title="Status">
                                            <span className="badge badge-info p-1 status">Replaced Product</span>
                                        </td>

                                        <td data-title="Description">
                                            There was already an existing active product, but some information has
                                            changed, and so the old product was deactivated and a new product was
                                            activated to take its place.
                                        </td>
                                    </tr>
                                    <tr>
                                        <td data-title="Status">
                                            <span className="badge p-1 status badge-blue">Activated Product</span>
                                        </td>

                                        <td data-title="Description">
                                            There wasn’t an already active product, but the require criteria now aligns,
                                            and so a new product was activated.
                                        </td>
                                    </tr>
                                    <tr>
                                        <td data-title="Status">
                                            <span className="badge badge-danger p-1 status">Deactivated Product</span>
                                        </td>

                                        <td data-title="Description">
                                            There was an active product, but now some required criteria does not align
                                            (due to the ‘Reason’ column), and so that product was deactivated.
                                        </td>
                                    </tr>
                                    <tr>
                                        <td data-title="Status">
                                            <span className="badge badge-warning p-1 status">No Product</span>
                                        </td>

                                        <td data-title="Description">
                                            There wasn’t an already active product, and the required criteria still does
                                            not align (due to the ‘Reason’ column), so there continues to be not active
                                            product).
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </AccordionPanel>
                    </AccordionItem>
                </Accordion>
            </section>
        </div>
    );
};

export default ProductSummary;
